import { useState, useEffect, useRef } from 'react'
import { Loader2, Trash2, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { getUserFingerprints, deleteUserFingerprint, type UserFingerprint } from '@/api/users'
import { getEnrollments } from '@/api/enrollments'
import type { FingerprintType, UserDeviceEnrollment } from '@isapi/shared'
import { ApiError } from '@/lib/api-error'

const FINGERPRINT_LABELS: Record<FingerprintType, string> = {
  normalFP: 'Normal',
  patrolFP: 'Patrulla',
  superFP: 'Super',
  stressFP: 'Estrés',
}

const FINGERPRINT_TYPES: FingerprintType[] = ['normalFP', 'patrolFP', 'superFP', 'stressFP']
const FINGER_SLOTS = Array.from({ length: 10 }, (_, i) => i + 1)

interface Props {
  userId: string
  proxyUrl: string
  proxyToken: string
}

type EnrollState = 'idle' | 'enrolling' | 'done' | 'error'

export function FingerprintList({ userId, proxyUrl, proxyToken }: Props) {
  const [fingerprints, setFingerprints] = useState<UserFingerprint[]>([])
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  const [deviceId, setDeviceId] = useState('')
  const [fingerprintId, setFingerprintId] = useState(1)
  const [fingerprintType, setFingerprintType] = useState<FingerprintType>('normalFP')
  const [enrolledDevices, setEnrolledDevices] = useState<UserDeviceEnrollment[]>([])
  const [enrollState, setEnrollState] = useState<EnrollState>('idle')
  const [progress, setProgress] = useState(0)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    loadFingerprints()
  }, [userId])

  useEffect(() => {
    if (!addOpen) return
    getEnrollments(userId)
      .then(enrs => {
        const active = enrs.filter(e => e.status !== 'blocked')
        setEnrolledDevices(active)
        if (active.length > 0) setDeviceId(active[0].device_id)
      })
      .catch(() => toast.error('Error al cargar dispositivos'))
  }, [addOpen, userId])

  function loadFingerprints() {
    setLoading(true)
    getUserFingerprints(userId)
      .then(r => setFingerprints(r.fingerprints))
      .catch(e => toast.error(e instanceof ApiError ? e.message : 'Error al cargar huellas'))
      .finally(() => setLoading(false))
  }

  async function handleDelete(fpId: number) {
    setDeletingId(fpId)
    try {
      await deleteUserFingerprint(userId, fpId)
      setFingerprints(prev => prev.filter(f => f.fingerprintId !== fpId))
      toast.success('Huella eliminada')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al eliminar')
    } finally {
      setDeletingId(null)
    }
  }

  async function handleStartEnroll() {
    if (!deviceId) return
    setEnrollState('enrolling')
    setProgress(0)
    try {
      await fetch(`${proxyUrl}/fingerprints/${deviceId}/enroll?employeeNo=${userId}&fingerprintId=${fingerprintId}&fingerprintType=${fingerprintType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${proxyToken}` },
      })
      pollRef.current = setInterval(async () => {
        try {
          const r = await fetch(`${proxyUrl}/fingerprints/${deviceId}/progress`, {
            headers: { Authorization: `Bearer ${proxyToken}` },
          })
          const data = await r.json() as { progressPercent: number; totalStatus: number }
          setProgress(data.progressPercent ?? 0)
          if (data.progressPercent >= 100) {
            clearInterval(pollRef.current!)
            await handleFinalize()
          }
        } catch {
          clearInterval(pollRef.current!)
          setEnrollState('error')
          toast.error('Error al obtener progreso del enrollment')
        }
      }, 2000)
    } catch {
      setEnrollState('error')
      toast.error('Error al iniciar enrollment')
    }
  }

  async function handleFinalize() {
    try {
      const qs = `employeeNo=${userId}&fingerprintId=${fingerprintId}&fingerprintType=${fingerprintType}`
      const r = await fetch(`${proxyUrl}/fingerprints/${deviceId}/finalize-enrollment?${qs}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${proxyToken}` },
      })
      if (!r.ok) {
        const body = await r.json() as { error?: string }
        throw new Error(body.error ?? 'Error al guardar huella')
      }
      setEnrollState('done')
      toast.success('Huella registrada correctamente')
      setAddOpen(false)
      loadFingerprints()
    } catch (e) {
      setEnrollState('error')
      toast.error(e instanceof Error ? e.message : 'Error al guardar huella')
    }
  }

  function handleCancel() {
    if (pollRef.current) clearInterval(pollRef.current)
    fetch(`${proxyUrl}/fingerprints/${deviceId}/enroll`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${proxyToken}` },
    }).catch(() => {})
    setEnrollState('idle')
    setAddOpen(false)
  }

  if (loading) return <p className="text-text-muted text-sm">Cargando huellas...</p>

  return (
    <div className="space-y-3">
      {fingerprints.length === 0 && (
        <p className="text-text-muted text-sm">Sin huellas registradas</p>
      )}
      {fingerprints.map(fp => (
        <div key={fp.fingerprintId} className="flex items-center justify-between py-2 border-b border-border last:border-0">
          <div>
            <span className="text-text-primary text-sm font-medium">
              Dedo {fp.fingerprintId} — {FINGERPRINT_LABELS[fp.fingerprintType as FingerprintType] ?? fp.fingerprintType}
            </span>
            <p className="text-text-muted text-xs">{new Date(fp.createdAt).toLocaleDateString()}</p>
          </div>
          <button
            onClick={() => handleDelete(fp.fingerprintId)}
            disabled={deletingId === fp.fingerprintId}
            className="text-text-muted hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-40"
          >
            {deletingId === fp.fingerprintId
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Trash2 className="w-4 h-4" />}
          </button>
        </div>
      ))}

      {!addOpen && (
        <button
          onClick={() => setAddOpen(true)}
          className="flex items-center gap-1 text-accent text-sm hover:text-accent-hover transition-colors"
        >
          <Plus className="w-4 h-4" /> Agregar huella
        </button>
      )}

      {addOpen && (
        <div className="border border-border rounded-lg p-4 space-y-3 bg-background">
          <div className="flex items-center justify-between">
            <p className="text-text-primary text-sm font-semibold">Agregar huella</p>
            <button onClick={handleCancel} className="text-text-muted hover:text-text-primary">
              <X className="w-4 h-4" />
            </button>
          </div>

          {enrollState === 'idle' && (
            <>
              <div className="space-y-2">
                <label className="text-xs text-text-muted block">Dispositivo</label>
                <select
                  value={deviceId}
                  onChange={e => setDeviceId(e.target.value)}
                  className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-text-primary"
                >
                  {enrolledDevices.map(d => (
                    <option key={d.device_id} value={d.device_id}>{d.device_name ?? d.device_id}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <div className="flex-1 space-y-2">
                  <label className="text-xs text-text-muted block">Dedo (slot)</label>
                  <select
                    value={fingerprintId}
                    onChange={e => setFingerprintId(Number(e.target.value))}
                    className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-text-primary"
                  >
                    {FINGER_SLOTS.map(n => (
                      <option key={n} value={n}>Dedo {n}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1 space-y-2">
                  <label className="text-xs text-text-muted block">Tipo</label>
                  <select
                    value={fingerprintType}
                    onChange={e => setFingerprintType(e.target.value as FingerprintType)}
                    className="w-full bg-surface border border-border rounded-md px-3 py-2 text-sm text-text-primary"
                  >
                    {FINGERPRINT_TYPES.map(t => (
                      <option key={t} value={t}>{FINGERPRINT_LABELS[t]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                onClick={handleStartEnroll}
                disabled={!deviceId}
                className="w-full bg-accent text-background text-sm font-semibold py-2 rounded-md hover:bg-accent-hover transition-colors disabled:opacity-40"
              >
                Iniciar enrollment
              </button>
            </>
          )}

          {enrollState === 'enrolling' && (
            <div className="space-y-2">
              <p className="text-text-primary text-sm">Pon el dedo en el dispositivo...</p>
              <div className="w-full bg-surface rounded-full h-2">
                <div
                  className="bg-accent h-2 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-text-muted text-xs text-right">{progress}%</p>
            </div>
          )}

          {enrollState === 'error' && (
            <div className="space-y-2">
              <p className="text-red-600 dark:text-red-400 text-sm">Error durante el enrollment.</p>
              <button
                onClick={() => setEnrollState('idle')}
                className="text-accent text-sm hover:text-accent-hover"
              >
                Intentar de nuevo
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
